// 行销来源（网址 ?referrer=）持久化：
// 进站时把渠道代码与完整入口网址写进 localStorage，注册时若当前网址上已经没有该参数
// （用户在站内换页、整页刷新、隔天回来再注册），仍能回退读取，避免来源标记丢失。
// 入口网址的域名按实际注册端归一；剔除登录凭证与内部传递参数。
// 保持零依赖：构建时同一份源码也编译到买即可官网 head，先于外部脚本执行。

export const REFERRER_STORAGE_KEY = 'signupReferrer'
export const SIGNUP_SOURCE_PARAM = 'signupSourceUrl'
// 30 天内有效，过期视为无来源
export const REFERRER_TTL_MS = 30 * 24 * 60 * 60 * 1000
// 沿用渠道代码长度限制；完整来源 URL 不使用此截断，避免丢失路径及推广参数。
const MAX_LENGTH = 200

function normalize (value) {
  return String(value || '').trim().slice(0, MAX_LENGTH)
}

function readParameter (name, url) {
  try {
    const match = new RegExp('[?&]' + name + '=([^&#]*)').exec(url || window.location.href)
    return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : ''
  } catch (error) {
    return ''
  }
}

// 读取网址上的 ?referrer=，兼容 hash 路由前后的参数。
export function readUrlReferrer (url) {
  if (typeof window === 'undefined' && !url) return ''
  return normalize(readParameter('referrer', url))
}

function isSameSite (target, current) {
  if (!/^https?:$/.test(target.protocol)) return false
  if (target.origin === current.origin) return true
  // 仅同品牌的正式 www/m 域名互认，外站同名参数不作为本站推广代码。
  const site = current.hostname.replace(/^(www|m)\./, '')
  return /^(magicbuy|kkren)\.com\.tw$/.test(site) &&
    ['www.' + site, 'm.' + site].includes(current.hostname) &&
    ['www.' + site, 'm.' + site].includes(target.hostname)
}

// 来源字段只留业务网址，普通 query 与会员 hash query 都不能包含认证凭据。
function cleanParameters (params) {
  const removed = /^(signupSourceUrl|accessToken|access_token|fbAccessToken|appleIdentityToken|token|password|captcha|code|state|redirect)$/i
  Array.from(params.keys()).forEach(key => { if (removed.test(key)) params.delete(key) })
}

function cleanUrl (value) {
  try {
    const url = new URL(value)
    if (!/^https?:$/.test(url.protocol)) return null
    url.username = ''
    url.password = ''
    cleanParameters(url.searchParams)
    const queryAt = url.hash.indexOf('?')
    if (queryAt >= 0) {
      const hash = url.hash
      const anchorAt = hash.indexOf('#', queryAt)
      const params = new URLSearchParams(hash.slice(queryAt + 1, anchorAt < 0 ? undefined : anchorAt))
      cleanParameters(params)
      url.hash = hash.slice(0, queryAt) + (params.toString() ? '?' + params.toString() : '') + (anchorAt < 0 ? '' : hash.slice(anchorAt))
    } else if (url.hash.indexOf('=') >= 0) {
      // OAuth 也可能直接使用 #access_token=...，没有会员路由的问号。
      const params = new URLSearchParams(url.hash.slice(1))
      const original = params.toString()
      cleanParameters(params)
      if (params.toString() !== original) url.hash = params.toString()
    }
    return url
  } catch (error) {
    return null
  }
}

function sourceRecord (value, current, expectedCode = '') {
  const source = cleanUrl(value)
  if (!source || !isSameSite(source, current)) return null
  const referrer = readUrlReferrer(source.href)
  if (!referrer || (expectedCode && referrer !== expectedCode)) return null
  // 保留落地页路径及参数，域名使用当前注册端；m 进站转 PC 后必须记为 www。
  source.protocol = current.protocol
  source.host = current.host
  return { referrer, sourceUrl: source.href }
}

function readDocumentSource (current, expectedCode = '') {
  if (typeof document === 'undefined' || !document.referrer) return null
  const previous = cleanUrl(document.referrer)
  if (!previous || !isSameSite(previous, current)) return null
  const code = readUrlReferrer(previous.href)
  if (!code || (expectedCode && code !== expectedCode)) return null
  // 上一页可能已是带内部来源参数的登录页，不能将登录页再套成来源。
  return sourceRecord(readParameter(SIGNUP_SOURCE_PARAM, document.referrer), current, code) || sourceRecord(previous.href, current, code)
}

export function readDocumentReferrer (url) {
  try {
    const record = readDocumentSource(new URL(url || window.location.href))
    return record ? record.referrer : ''
  } catch (error) {
    return ''
  }
}

function resolveSourceRecord (url) {
  try {
    const current = new URL(url || window.location.href)
    const code = readUrlReferrer(current.href)
    const saved = readStoredRecord()
    const stored = saved && sourceRecord(saved.sourceUrl, current, saved.referrer)
    if (stored) stored.savedAt = saved.savedAt
    if (code) {
      const carried = sourceRecord(readParameter(SIGNUP_SOURCE_PARAM, current.href), current, code)
      if (carried) return carried
      // 兼容旧版站内注册链接：同渠道时从真实上一页/缓存补出入口。
      const previous = typeof document !== 'undefined' && cleanUrl(document.referrer)
      if (/^\/(login|register)\/?$/.test(current.pathname) && previous && isSameSite(previous, current)) {
        const fromDocument = readDocumentSource(current, code)
        if (fromDocument) return fromDocument
        if (stored && stored.referrer === code && !readUrlReferrer(previous.href)) return stored
      }
      // 独立进站或显式新活动使用本次页面；同渠道的新落地页也更新路径与 UTM。
      return sourceRecord(current.href, current, code)
    }
    return stored || readDocumentSource(current)
  } catch (error) {
    return null
  }
}

export function resolveReferrerCode (url) {
  const record = resolveSourceRecord(url)
  return record ? record.referrer : ''
}

// 给本站登录/注册链接同时补渠道与入口 URL；显式活动链接保留自己的来源。
export function withSignupReferrer (href, { preserveSource = false } = {}) {
  if (!href || typeof window === 'undefined') return href
  try {
    const current = new URL(window.location.href)
    const target = new URL(href, current)
    if (!isSameSite(target, current) || !/^\/(login|register)\/?$/.test(target.pathname)) return href
    const explicitCode = readUrlReferrer(target.href)
    const record = resolveSourceRecord()
    if (!record || (explicitCode && (!preserveSource || explicitCode !== record.referrer))) return href
    const source = sourceRecord(record.sourceUrl, target, record.referrer)
    if (!source) return href
    target.searchParams.set('referrer', source.referrer)
    target.searchParams.set(SIGNUP_SOURCE_PARAM, source.sourceUrl)
    return /^(https?:)?\/\//.test(href) ? target.href : target.pathname + target.search + target.hash
  } catch (error) {
    return href
  }
}

// 活动页注册链接的兜底渠道：进站网址自带 ?referrer= 时沿用进站来源（渠道与入口 URL），
// 没有时才使用活动渠道；不读取历史缓存，避免旧渠道顶替本活动。
export function withFallbackReferrer (href, fallback) {
  if (!href || typeof window === 'undefined') return href
  try {
    const current = new URL(window.location.href)
    const target = new URL(href, current)
    if (!isSameSite(target, current) || !/^\/(login|register)\/?$/.test(target.pathname)) return href
    const format = () => /^(https?:)?\/\//.test(href) ? target.href : target.pathname + target.search + target.hash
    target.searchParams.delete('referrer')
    target.searchParams.delete(SIGNUP_SOURCE_PARAM)
    const base = format()
    const code = readUrlReferrer(current.href)
    if (code) {
      const carried = withSignupReferrer(base)
      if (carried !== base) return carried
    }
    const referrer = code || normalize(fallback)
    if (!referrer) return base
    target.searchParams.set('referrer', referrer)
    return format()
  } catch (error) {
    return href
  }
}

// 在 head 注册捕获事件，覆盖预渲染按钮、动态组件、新分页及右键复制链接。
// 带 data-referrer-fallback 的活动链接按兜底渠道规则处理，预渲染快照里的活动渠道不会覆盖进站来源。
// 不阻止浏览器默认导航；即使 Vue/外部脚本未加载，仍可携带来源进入注册页。
export function installSignupReferrerLinks () {
  if (typeof document === 'undefined' || document.__signupReferrerLinksInstalled) return
  document.__signupReferrerLinksInstalled = true
  const updateLink = event => {
    let el = event.target
    while (el && el.nodeType !== 9) {
      if (el.tagName === 'A') {
        const href = el.getAttribute('href')
        const original = el.__signupReferrerApplied === href ? el.__signupReferrerOriginal : href
        const fallback = el.getAttribute('data-referrer-fallback')
        const next = fallback ? withFallbackReferrer(original, fallback) : withSignupReferrer(original)
        if (next !== href && next) el.setAttribute('href', next)
        el.__signupReferrerOriginal = original
        el.__signupReferrerApplied = next
        return
      }
      el = el.parentNode
    }
  }
  ;['click', 'auxclick', 'contextmenu'].forEach(name => document.addEventListener(name, updateLink, true))
}

// 采集完整入口；head、mount 和登录页重复执行时不覆盖入口或延长原记录期限。
export function rememberReferrer (url) {
  const record = resolveSourceRecord(url)
  if (!record) return ''
  try {
    const saved = readStoredRecord()
    const savedAt = saved && saved.referrer === record.referrer && saved.sourceUrl === record.sourceUrl ? saved.savedAt : (record.savedAt || Date.now())
    localStorage.setItem(REFERRER_STORAGE_KEY, JSON.stringify({ version: 2, referrer: record.referrer, sourceUrl: record.sourceUrl, savedAt }))
  } catch (error) {
    // localStorage 不可用（隐私模式 / WebView 限制）时只影响持久化，不影响本次注册
  }
  return readUrlReferrer(url) ? record.referrer : ''
}

// 读取已持久化的来源；过期或格式不对时清掉并返回空字符串
function readStoredRecord (now = Date.now()) {
  try {
    const raw = localStorage.getItem(REFERRER_STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    const referrer = normalize(data && data.referrer)
    const savedAt = Number(data && data.savedAt) || 0
    if (!referrer || now - savedAt > REFERRER_TTL_MS || now < savedAt - REFERRER_TTL_MS) {
      localStorage.removeItem(REFERRER_STORAGE_KEY)
      return null
    }
    // 旧缓存只有渠道码，保留兼容读取，但不能据此拼造原始入口网址。
    return { referrer, sourceUrl: typeof data.sourceUrl === 'string' ? data.sourceUrl : '', savedAt }
  } catch (error) {
    return null
  }
}

export function getStoredReferrer (now = Date.now()) {
  const record = readStoredRecord(now)
  return record ? record.referrer : ''
}

export function clearStoredReferrer () {
  try {
    localStorage.removeItem(REFERRER_STORAGE_KEY)
  } catch (error) {
    // 忽略存储异常
  }
}

// 注册时提交完整推广入口，域名为实际注册端；无推广来源时使用完整上一页网址。
export function resolveSignupReferer (url) {
  const record = resolveSourceRecord(url)
  if (record) return record.sourceUrl
  const previous = typeof document !== 'undefined' && cleanUrl(document.referrer)
  return previous ? previous.href : ''
}
